const SEPARATOR = /[\s/_.\-›]/;

const CHAR_POINTS = 1;
const WORD_START_BONUS = 8;
const CONSECUTIVE_BONUS = 6;
const MAX_GAP_PENALTY = 5;
const PREFIX_BONUS = 20;
// prefers shorter candidates when everything else is equal
const LENGTH_PENALTY = 0.01;

const isWordStart = (text: string, index: number) => index === 0 || SEPARATOR.test(text[index - 1]);

function scoreFromStart(text: string, query: string, start: number): number | null {
  let score = 0;
  let previous = -1;

  for (const char of query) {
    // the first character is pinned to `start`
    const found = text.indexOf(char, previous === -1 ? start : previous + 1);
    if (found === -1) {
      return null;
    }

    score += CHAR_POINTS;
    if (isWordStart(text, found)) {
      score += WORD_START_BONUS;
    }
    if (previous !== -1) {
      score +=
        found === previous + 1
          ? CONSECUTIVE_BONUS
          : -Math.min(found - previous - 1, MAX_GAP_PENALTY);
    }
    previous = found;
  }

  return score;
}

/**
 * The query characters must appear in the text in order,
 * not necessarily next to each other (`buser` matches `bo_users`).
 * Higher is better, `null` means no match.
 * Both arguments are expected to be lowercase already.
 */
export function fuzzyScore(text: string, query: string): number | null {
  let best: number | null = null;

  // the rest is matched greedily, so try each place the first character occurs and keep the best
  for (
    let start = text.indexOf(query[0]);
    start !== -1;
    start = text.indexOf(query[0], start + 1)
  ) {
    const score = scoreFromStart(text, query, start);
    if (score !== null && (best === null || score > best)) {
      best = score;
    }
  }

  if (best === null) {
    return null;
  }
  return best + (text.startsWith(query) ? PREFIX_BONUS : 0) - text.length * LENGTH_PENALTY;
}

/**
 * Lower-cases and strips accents, so that "cree" matches "Créé après" in the French BO.
 */
const normalizeForSearch = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * Keeps the items whose texts fuzzy-match the query, best matches first (ties keep the input
 * order). An item scores the best of its texts, e.g. a label and a technical name, so `uid` finds
 * "User ID" and `kycstate` finds `kyc_state`. Whitespace in the query is ignored.
 */
export function fuzzyFilter<T>(
  items: readonly T[],
  rawQuery: string,
  getTexts: (item: T) => readonly string[],
): T[] {
  const query = normalizeForSearch(rawQuery).replace(/\s+/g, '');
  if (query === '') {
    return [...items];
  }

  return items
    .flatMap((item, index) => {
      const scores = getTexts(item)
        .map((text) => fuzzyScore(normalizeForSearch(text), query))
        .filter((score): score is number => score !== null);
      return scores.length === 0 ? [] : [{ item, score: Math.max(...scores), index }];
    })
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ item }) => item);
}
